<script lang="ts" setup>
import { computed, ref } from "vue";
import {
  HousingApplication,
  HousingApplicationStatus,
  HousingOrg,
  HousingSite,
  HousingSiteResult,
} from "~/api";
import { useHousingAplicationWizard } from "~/YYC_Housing/composables/useHousingApplicationWizard";
import { useHousingOrgStore } from "~/YYC_Housing/stores/HousingOrgStore";
import { useHousingSiteStore } from "~/YYC_Housing/stores/HousingSiteStore";
import { ApplicationWizardStep } from "../types/plusTypes";

const housingOrgStore = useHousingOrgStore();
const housingSiteStore = useHousingSiteStore();

interface HousingSiteExtended extends HousingSite {
  housingOrgCode: string;
  org?: HousingOrg;
}

const props = defineProps<{
  preferences: HousingSiteResult[];
  application: HousingApplication;
}>();

const { stepStatusMap, handleGotoStep } = useHousingAplicationWizard(
  props.application,
);

const expanded = ref(false);

const preferredHousingSites = computed<HousingSiteExtended[]>(() => {
  return housingSiteStore.List.map(
    (hs) =>
      ({
        ...hs,
        org: housingOrgStore.List.find((org) => org.guid === hs.housingOrgGuid),
      }) as HousingSiteExtended,
  ).filter((hs) =>
    props.preferences.some(
      (p) => p.siteCode === hs.code && p.hmb === hs.housingOrgCode,
    ),
  );
});
</script>

<template>
  <div
    class="LocationPreferences"
    :class="{ Completed: preferredHousingSites.length > 0 }"
  >
    <el-collapse class="accordion" @change="expanded = !expanded">
      <el-collapse-item name="2">
        <template #title>
          <div class="select-properties-title">Select Properties</div>
          <div class="preferencesCount">
            <div class="preferencesCountWording">
              <span>{{
                stepStatusMap[ApplicationWizardStep.SelectLocations]
                  .statusDisplayText
              }}</span>
              <span
                v-if="
                  !stepStatusMap[ApplicationWizardStep.SelectLocations]
                    .completed
                "
                class="preferencesCountInstructions"
                >Click the "Start" button to select properties</span
              >
            </div>
            <el-button
              v-if="preferredHousingSites.length"
              class="expand-btn"
              type="primary"
              size="small"
            >
              {{ expanded ? "Hide List" : "Show List" }}
            </el-button>
          </div>
          <el-button
            class="editButton"
            type="primary"
            :disabled="application.status !== HousingApplicationStatus.DRAFT"
            @click="() => handleGotoStep(ApplicationWizardStep.SelectLocations)"
          >
            <template
              v-if="
                stepStatusMap[ApplicationWizardStep.SelectLocations].completed
              "
            >
              Change
            </template>
            <template v-else> Start </template>
          </el-button>
        </template>
        <div
          v-for="site in preferredHousingSites"
          :key="site.guid"
          class="location summary"
        >
          <div class="location-header">
            <span>{{ site.org?.name }}</span>
            <span class="location-title">{{ site.name }}</span>
          </div>
        </div>
      </el-collapse-item>
    </el-collapse>
    <slot name="inputs"> </slot>
  </div>
</template>

<style lang="less">
.LocationPreferences {
  display: flex;
  flex-direction: column;

  .el-collapse-item.is-active .el-collapse-item__header {
    background-color: inherit;
  }

  .el-collapse-item__header {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
    .el-icon {
      display: none;
    }

    .el-collapse-item__title {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-areas:
        "title total edit"
        "x x expand";
      justify-items: center;
      align-items: center;
    }

    .select-properties-title {
      grid-area: title;
      font-weight: 500;
      justify-self: start;
      @media (max-width: 700px) {
        font-size: 1em;
      }
      @media (max-width: 500px) {
        font-size: 0.65em;
        .editButton {
          font-size: 10px;
        }
      }
    }

    .preferencesCount {
      grid-area: total;
      display: flex;
      gap: 16px;
      align-items: center;

      .preferencesCountWording {
        display: flex;
        flex-direction: column;
        align-items: center;
        @media (max-width: 700px) {
          font-size: 0.8em;
        }
        @media (max-width: 500px) {
          font-size: 0.45em;
        }
        .preferencesCountInstructions {
          font-size: 0.8em;
        }
      }
    }

    .editButton {
      grid-area: edit;
      justify-self: end;
    }

    .expand-btn {
      grid-area: expand;
      justify-self: end;
    }
  }

  .location.summary {
    border: none;
    background: transparent;
    padding: 0;

    &:first-child {
      margin-top: 1rem;
    }

    .location-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #ddd;
      .location-title {
        font-weight: bold;
      }
    }
  }

  .el-collapse-item__wrap {
    background-color: inherit;
  }

  .el-collapse-item__content {
    padding-bottom: 0;
  }

  .accordion {
    width: 100%;
    --el-collapse-border-color: transparent;
    .el-collapse-item__header {
      font-weight: normal;
      color: black;
    }
    .el-collapse-item__arrow {
      margin-left: 16px;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      font-size: 1rem;
      color: black;

      li {
        display: flex;
        justify-content: space-between;
        padding: 0.5rem;
        border-bottom: 1px solid #ddd;
      }
    }
  }
}
</style>
